import { cn } from '@/lib/utils'
import {
  motion,
  useReducedMotion,
  useSpring
} from 'framer-motion'
import { type RefObject, useEffect, useRef, useState } from 'react'

/**
 * Each agent is a little companion: a flat shape in its one colour with two
 * plain eyes (white on dark colours, black on light ones). Nothing else. The
 * eyes blink, glance around and follow your cursor; they look up while
 * thinking, squint while using tools and squeeze happy while replying.
 */
type Shape = 'circle' | 'squircle' | 'drop' | 'bubble' | 'dome'

// Static outlines, all inside the same square so every size lines up.
const SHAPES: Record<Shape, string> = {
  circle: '50%',
  squircle: '32%',
  drop: '50% 50% 50% 50% / 58% 58% 42% 42%',
  bubble: '50% 50% 50% 12%',
  dome: '50% 50% 26% 26% / 62% 62% 38% 38%'
}

interface Palette {
  name: string
  color: string
  shape: Shape
}

const PALETTES: Record<string, Palette> = {
  chief_of_staff: { name: 'Chief of Staff', color: '#6d1a2e', shape: 'squircle' },
  paid_media: { name: 'Paid Media Buyer', color: '#2f5bea', shape: 'bubble' },
  merchant_recruitment: { name: 'Merchant Recruiter', color: '#0e9f6e', shape: 'dome' },
  content_strategy: { name: 'Content Strategist', color: '#e8457c', shape: 'drop' },
  ad_creative: { name: 'Ad Creative', color: '#8b5cf6', shape: 'bubble' },
  billing: { name: 'Billing & Payouts', color: '#0f766e', shape: 'squircle' },
  router: { name: 'Front Door', color: '#737373', shape: 'circle' },
  consumer_support: { name: 'Customer Care', color: '#0ea5e9', shape: 'dome' },
  logistics_support: { name: 'Orders & Delivery', color: '#f97316', shape: 'drop' },
  booking_support: { name: 'Bookings', color: '#c026d3', shape: 'circle' },
  merchant_success: { name: 'Merchant Success', color: '#4f46e5', shape: 'dome' },
  merchant_ads: { name: 'Merchant Ad Strategist', color: '#facc15', shape: 'bubble' },
  safety: { name: 'Trust & Safety', color: '#1e3a8a', shape: 'squircle' },
  listings: { name: 'Listings Keeper', color: '#65a30d', shape: 'drop' },
  legal: { name: 'Legal & Compliance', color: '#92400e', shape: 'circle' }
}

/** White eyes on dark colours, black eyes on light ones. */
function eyeColor(hex: string) {
  const n = Number.parseInt(hex.slice(1), 16)
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255]
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62 ? '#0a0a0a' : '#ffffff'
}

const BY_NAME: Record<string, string> = Object.fromEntries(
  Object.entries(PALETTES).map(([key, p]) => [p.name, key])
)

export const agentKeyFromName = (name?: string | null) =>
  name ? (BY_NAME[name] ?? '') : ''

type Size = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
const PX: Record<Size, number> = { xs: 20, sm: 28, md: 36, lg: 48, xl: 112 }

export type OrbState =
  | 'idle'
  | 'thinking'
  | 'working'
  | 'replying'
  | 'waiting'
  | 'error'

export const STATE_LABEL: Record<OrbState, string> = {
  idle: 'Ready',
  thinking: 'Thinking',
  working: 'Using tools',
  replying: 'Replying',
  waiting: 'Waiting for you',
  error: 'Something went wrong'
}

/** True for a few seconds after an answer arrives, so the companion can smile while "replying". */
export function useFresh(createdAt?: string, ms = 4000) {
  const age = createdAt ? Date.now() - new Date(createdAt).getTime() : ms
  const [fresh, setFresh] = useState(age < ms)
  useEffect(() => {
    if (!fresh) return
    const id = window.setTimeout(() => setFresh(false), Math.max(0, ms - age))
    return () => window.clearTimeout(id)
  }, [fresh, age, ms])
  return fresh
}

export function AgentAvatar({
  agentKey,
  name,
  size = 'md',
  working = false,
  state,
  className
}: {
  agentKey?: string | null
  name?: string | null
  size?: Size
  /** Shorthand for state="working". */
  working?: boolean
  state?: OrbState
  className?: string
}) {
  const key = agentKey || agentKeyFromName(name)
  const palette = PALETTES[key] ?? PALETTES.router
  return (
    <Companion
      palette={palette}
      px={PX[size]}
      state={state ?? (working ? 'working' : 'idle')}
      label={palette.name || name || ''}
      className={className}
    />
  )
}

/** Chief of Staff's companion, for places that show the lead without a key at hand. */
export function ChiefOrb({
  size = 'md',
  working = false,
  state,
  className
}: { size?: Size; working?: boolean; state?: OrbState; className?: string }) {
  return (
    <AgentAvatar
      agentKey="chief_of_staff"
      size={size}
      working={working}
      state={state}
      className={className}
    />
  )
}

// --- Shared cursor --------------------------------------------------------------
// One window listener for every companion on the page, so they all watch the mouse.

const pointer = { x: 0, y: 0, at: 0 }
const watchers = new Set<() => void>()
function onPointer(e: PointerEvent) {
  pointer.x = e.clientX
  pointer.y = e.clientY
  pointer.at = performance.now()
  for (const w of watchers) w()
}
function watchPointer(fn: () => void) {
  if (watchers.size === 0) window.addEventListener('pointermove', onPointer, { passive: true })
  watchers.add(fn)
  return () => {
    watchers.delete(fn)
    if (watchers.size === 0) window.removeEventListener('pointermove', onPointer)
  }
}

const rand = (min: number, max: number) => min + Math.random() * (max - min)

function Companion({
  palette,
  px,
  state,
  label,
  className
}: {
  palette: Palette
  px: number
  state: OrbState
  label: string
  className?: string
}) {
  const reduce = Boolean(useReducedMotion())
  const ref = useRef<HTMLSpanElement>(null)

  return (
    <span
      ref={ref}
      className={cn(
        'relative inline-flex shrink-0 overflow-hidden ring-1 ring-black/5 ring-inset dark:ring-white/15',
        className
      )}
      style={{
        width: px,
        height: px,
        background: palette.color,
        borderRadius: SHAPES[palette.shape],
        filter: state === 'error' ? 'grayscale(1)' : undefined
      }}
      title={`${label} · ${STATE_LABEL[state]}`}
      role="img"
      aria-label={`${label}, ${STATE_LABEL[state].toLowerCase()}`}
    >
      <Eyes px={px} state={state} reduce={reduce} anchor={ref} color={eyeColor(palette.color)} />
    </span>
  )
}

// --- Eyes ---------------------------------------------------------------------

function Eyes({
  px,
  state,
  reduce,
  anchor,
  color
}: {
  px: number
  state: OrbState
  reduce: boolean
  anchor: RefObject<HTMLSpanElement | null>
  color: string
}) {
  // Plain oval eyes, with floors so the smallest companions still read.
  const w = Math.max(3, px * 0.13)
  const h = Math.max(4.5, px * 0.21)
  const gap = Math.max(3, px * 0.15)
  const reach = { x: px * 0.1, y: px * 0.08 }

  const x = useSpring(0, { stiffness: 380, damping: 28 })
  const y = useSpring(0, { stiffness: 380, damping: 28 })
  const [blink, setBlink] = useState(false)

  // Look where the cursor is. When the cursor rests, fall back to the state's own gaze.
  useEffect(() => {
    if (reduce) {
      x.set(0)
      y.set(0)
      return
    }
    const follow = state === 'idle' || state === 'waiting'
    let frame = 0
    const look = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const el = anchor.current
        if (!el || !follow) return
        const r = el.getBoundingClientRect()
        const dx = pointer.x - (r.left + r.width / 2)
        const dy = pointer.y - (r.top + r.height / 2)
        const d = Math.hypot(dx, dy) || 1
        const pull = Math.min(1, d / 160)
        x.set((dx / d) * reach.x * pull)
        y.set((dy / d) * reach.y * pull)
      })
    }
    const stop = watchPointer(look)

    let timer = 0
    let side = 1
    const wander = () => {
      let wait = rand(1.4, 3)
      const resting = performance.now() - pointer.at > 2500
      if (state === 'thinking') {
        side = -side
        x.set(side * reach.x * 0.7)
        y.set(-reach.y)
        wait = rand(0.8, 1.3)
      } else if (state === 'working') {
        side = -side
        x.set(side * reach.x)
        y.set(reach.y * 0.4)
        wait = 0.38
      } else if (state === 'replying' || state === 'error') {
        x.set(0)
        y.set(0)
      } else if (resting) {
        const centre = Math.random() < 0.35
        x.set(centre ? 0 : rand(-1, 1) * reach.x)
        y.set(centre ? 0 : rand(-0.7, 0.7) * reach.y)
      }
      timer = window.setTimeout(wander, wait * 1000)
    }
    wander()
    return () => {
      stop()
      cancelAnimationFrame(frame)
      window.clearTimeout(timer)
    }
  }, [state, reduce, anchor, reach.x, reach.y, x, y])

  // Blink now and then, sometimes twice.
  useEffect(() => {
    if (reduce || state === 'replying' || state === 'error') return
    let timer = 0
    const close = (times: number) => {
      setBlink(true)
      timer = window.setTimeout(() => {
        setBlink(false)
        timer =
          times > 1
            ? window.setTimeout(() => close(times - 1), 150)
            : window.setTimeout(
                () => close(Math.random() < 0.25 ? 2 : 1),
                rand(2.2, 5.5) * 1000
              )
      }, 120)
    }
    timer = window.setTimeout(() => close(1), rand(0.8, 3) * 1000)
    return () => window.clearTimeout(timer)
  }, [state, reduce])

  // Height of the eyes per mood: squint, happy squeeze, flat when something broke.
  const open =
    state === 'error'
      ? 0.22
      : state === 'replying'
        ? 0.5
        : state === 'working'
          ? 0.55
          : state === 'thinking'
            ? 0.85
            : 1
  const grow = state === 'waiting' ? 1.15 : 1

  return (
    <motion.span
      className="pointer-events-none absolute inset-0 flex items-center justify-center"
      style={{ gap, x, y }}
      aria-hidden
    >
      {[0, 1].map((i) => (
        <motion.span
          key={i}
          className="block rounded-full"
          style={{ width: w, height: h, background: color }}
          animate={{ scaleY: blink ? 0.1 : open, scale: grow }}
          transition={{ type: 'spring', stiffness: blink ? 1400 : 420, damping: 30 }}
        />
      ))}
    </motion.span>
  )
}
